import _styled, { css as _css, default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import React from "react";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Headline } from "~/features/ui/headline";
import { Eyebrow } from "~/features/ui/eyebrow";
import { Slider } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
const cardStyle = /*#__PURE__*/_css(["", ";"], {
  "position": "relative"
});
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type ExternalProductsBlockViewProps<TCard = unknown> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  cards?: TCard[] | null;
  eyebrow?: string | null;
  getCardKey?: (card: TCard, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  mode?: string | null;
  renderCard: (card: TCard, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function ExternalProductsBlockView<TCard = unknown>({
  anchor,
  cards = [],
  eyebrow,
  getCardKey,
  hasTopDivider = true,
  headline,
  mode = "light",
  renderCard,
  sectionProps,
  theme = "white"
}: ExternalProductsBlockViewProps<TCard>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const items = cards ?? [];
  if (!hasItems(items)) {
    return null;
  }
  const hasHeaderData = Boolean(eyebrow || headline);
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css2={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, themeStyle.background]}>
      <Wrap mode={mode as Mode}>
        {/* Header */}
        {hasHeaderData && <_StyledDiv $_css3={[{
        "position": "relative",
        "zIndex": "10",
        "display": "flex",
        "flexDirection": "column",
        "gap": "1.6rem",
        "borderBottomWidth": "1px",
        "padding": "4rem",
        "@media (min-width: 1024px)": {
          "flexDirection": "row",
          "gap": "2rem",
          "padding": "8rem"
        }
      }, themeStyle.border, themeStyle.background]}>
            <_StyledGrid>
              <_StyledDiv2 $_css4={[{
            "gridColumn": "1 / -1",
            "@media (min-width: 1024px)": {
              "gridColumn": "span 12 / span 12"
            }
          }]}>
                {eyebrow && <Eyebrow text={eyebrow} mode={mode as Mode} />}
                {headline && <_StyledHeadline type="h2" text={headline} />}
              </_StyledDiv2>
            </_StyledGrid>
          </_StyledDiv>}

        {/* Mobile Slider */}
        <_StyledDiv3 $_css5={[{
        "position": "relative",
        "zIndex": "10",
        "@media (min-width: 1024px)": {
          "display": "none"
        }
      }, themeStyle.border, themeStyle.background]}>
          <Slider onMove={splide => setActiveSlideIndex(splide.Components.Controller.getIndex())} options={{
          fixedWidth: "90%"
        }}>
            {items.map((card, idx) => <_StyledSliderSlide key={getCardKey?.(card, idx) || idx} $_css6={[{
            "padding": "1.6rem",
            "&:not(:first-child)": {
              "borderLeftWidth": "1px"
            }
          }, themeStyle.background, themeStyle.border]}>
                {renderCard(card, idx)}
              </_StyledSliderSlide>)}
          </Slider>
          <_StyledDiv4 $_css7={[{
          "paddingLeft": "1.6rem",
          "paddingRight": "1.6rem",
          "paddingTop": "3.2rem",
          "paddingBottom": "3.2rem"
        }, themeStyle.background, themeStyle.border]}>
            <Progress activeSlideIndex={activeSlideIndex} totalSlides={items.length} />
          </_StyledDiv4>
        </_StyledDiv3>

        {/* Desktop Grid */}
        <_StyledDiv5>
          <_StyledGrid2 $_css8={[{
          "position": "relative",
          "zIndex": "10"
        }, themeStyle.border]}>
            {items.map((card, idx) => <_StyledDiv6 key={getCardKey?.(card, idx) || idx} $_css9={[{
            "gridColumn": "span 4 / span 4",
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px",
            "borderColor": "rgb(0 7 4 / 0.1)",
            "padding": "4rem",
            "@media (min-width: 1024px)": {
              "borderLeftWidth": "0px"
            }
          }, themeStyle.outline, themeStyle.border, idx % 4 === 0 && {
            "borderLeftWidth": "1px !important",
            "borderLeftColor": "rgb(0 7 4 / 0.1)"
          }, cardStyle, themeStyle.background]}>
                {renderCard(card, idx)}
              </_StyledDiv6>)}
          </_StyledGrid2>
        </_StyledDiv5>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1h7oshp-0"
})`${p => p.$_css2}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1h7oshp-1"
})`${p => p.$_css3}`;
var _StyledGrid = _styled3(Grid).withConfig({
  componentId: "sc-sf-1h7oshp-2"
})({
  "rowGap": "1.6rem"
});
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-1h7oshp-3"
})`${p => p.$_css4}`;
var _StyledHeadline = _styled5(Headline).withConfig({
  componentId: "sc-sf-1h7oshp-4"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledDiv3 = _styled6("div").withConfig({
  componentId: "sc-sf-1h7oshp-5"
})`${p => p.$_css5}`;
var _StyledSliderSlide = _styled7(Slider.Slide).withConfig({
  componentId: "sc-sf-1h7oshp-6"
})`${p => p.$_css6}`;
var _StyledDiv4 = _styled8("div").withConfig({
  componentId: "sc-sf-1h7oshp-7"
})`${p => p.$_css7}`;
var _StyledDiv5 = _styled9("div").withConfig({
  componentId: "sc-sf-1h7oshp-8"
})({
  "display": "none",
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem",
  "@media (min-width: 1024px)": {
    "display": "block",
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledGrid2 = _styled10(Grid).withConfig({
  componentId: "sc-sf-1h7oshp-9"
})`${p => p.$_css8}`;
var _StyledDiv6 = /*#__PURE__*/_styled11("div").withConfig({
  displayName: "external-products___StyledDiv6",
  componentId: "sc-167am3r-0"
})(["", ""], p => p.$_css9);